import { useQuery } from '@tanstack/react-query';
import type { ReactElement } from 'react';

import { fetchSurveyResults } from '@/features/surveys/api';
import { ApiError } from '@/lib/api/http';

/** 集計結果（result_scope に従って閲覧可否がAPI側で判定される / DECISIONS D-018）. */
export function SurveyResultsView({ eventSurveyId }: { eventSurveyId: string }): ReactElement {
  const query = useQuery({
    queryKey: ['survey-results', eventSurveyId],
    queryFn: () => fetchSurveyResults(eventSurveyId),
  });

  if (query.isPending) return <p className="text-wptext-muted">読み込み中…</p>;
  if (query.isError) {
    if (query.error instanceof ApiError && query.error.status === 403) {
      return <p className="text-wptext-muted">この課題・アンケートの集計結果を閲覧する権限がありません。</p>;
    }
    return <p className="text-wpnotice-error">読み込みに失敗しました。</p>;
  }

  const results = query.data;

  return (
    <div>
      <p className="mb-3 text-wptext-muted">提出済み: {results.submitted_count}件</p>
      <div className="flex flex-col gap-4">
        {results.questions.map((q) => (
          <div key={q.question_id} className="rounded-sm border border-wpborder bg-white p-3">
            <p className="mb-1 font-semibold text-wptext-strong">{q.label}</p>
            <p className="mb-2 text-wptext-muted">
              回答: {q.answered_count}件 / 未回答: {q.skipped_count}件
            </p>
            {q.matrix_counts ? (
              <div className="overflow-x-auto">
                <table className="border-collapse text-left">
                  <tbody>
                    {Object.entries(q.matrix_counts).map(([row, colCounts]) => (
                      <tr key={row} className="border-b border-wpborder">
                        <td className="px-2 py-1 text-wptext-strong">{row}</td>
                        <td className="px-2 py-1 text-wptext-muted">
                          {Object.entries(colCounts)
                            .map(([col, count]) => `${col}: ${count}件`)
                            .join(' / ') || '回答なし'}
                        </td>
                      </tr>
                    ))}
                    {Object.keys(q.matrix_counts).length === 0 && (
                      <tr>
                        <td className="px-2 py-1 text-wptext-muted">回答なし</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            ) : q.option_counts ? (
              <ul className="flex flex-col gap-1">
                {Object.entries(q.option_counts).map(([option, count]) => (
                  <li key={option}>
                    {option}: {count}件
                  </li>
                ))}
                {Object.keys(q.option_counts).length === 0 && <li className="text-wptext-muted">回答なし</li>}
              </ul>
            ) : (
              <ul className="flex flex-col gap-1">
                {(q.values ?? []).map((v, i) => (
                  <li key={i} className="text-wptext-muted">
                    {typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v)}
                  </li>
                ))}
                {(q.values ?? []).length === 0 && <li className="text-wptext-muted">回答なし</li>}
              </ul>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
